/**
 * Permission catalog — FE mirror of `app.domains.auth.permissions.Permission`
 * + `ROLE_PERMISSIONS` (TRD-02 §2.3).
 *
 * Hand-authored on purpose (not generated) so the FE bundle can render
 * the Designation editor offline-first without paying a `/auth/permissions/catalog`
 * round-trip on the open-the-sheet hot path. The trade-off: when the BE
 * adds or renames a permission, this file MUST be kept in sync. A drift
 * test could be added in Phase F-followup; until then the explicit
 * `__all_permissions_match_be__` constant below acts as a unit-test anchor
 * (it's referenced by the FE Playwright spec — if the BE adds a new
 * permission and we forget to update this file, the spec fails because
 * the editor row count won't match the BE catalog `/auth/me/permissions`
 * response shape).
 *
 * Iron Rule #5: permission identifiers are stable strings — never rename.
 */

import type { TenantVerticalLiteral, UserRole } from "./types/index.js";

/** Stable permission identifiers — must match BE `Permission` enum values. */
export const PERMISSION_IDS = [
  // Student management
  "view_students",
  "manage_students",
  // Phase H6b: bulk CSV import via /me/students/preview + commit.
  // Side effect (Student inserts + parent/admission stubs) is identical
  // to manage_students writes, so the admin role gets both by default
  // (mirrors `ROLE_PERMISSIONS["admin"]` BE-side). Surfaced separately
  // so SAs / owners can scope a staff override that allows bulk import
  // without the rest of manage_students' write surface.
  "import_students",
  // Academic
  "view_batches",
  "manage_batches",
  "view_timetable",
  "manage_timetable",
  "view_subjects",
  "manage_subjects",
  // Assessment
  "create_tests",
  "grade_tests",
  "view_results",
  "manage_results",
  "create_assignments",
  "grade_assignments",
  "manage_question_bank",
  // Content
  "upload_content",
  "manage_courses",
  // Live classes
  "start_live_class",
  "join_live_class",
  // Communication
  "send_announcements",
  "view_announcements",
  "manage_notifications",
  "parent_communication",
  // Operations
  "manage_fees",
  "view_fees",
  "pay_fees",
  "manage_attendance",
  "view_attendance",
  "manage_transport",
  "manage_admissions",
  "manage_certificates",
  "manage_payroll",
  // Payroll — owner finances (Phase SC-2c). Monthly pay runs feeding the
  // P&L staff-cost line. Admin-ceiling; mirrors `Permission.VIEW_PAYROLL`
  // / `MANAGE_PAYROLL` on the BE.
  "view_payroll",
  // Team pay + attendance (Phase SC-22d). Admin-ceiling; mirrors
  // `Permission.VIEW_TEAM_PAY` / `MANAGE_TEAM_PAY` /
  // `MANAGE_TEAM_ATTENDANCE` / `APPROVE_TEAM_ATTENDANCE` on the BE.
  "view_team_pay",
  "manage_team_pay",
  "manage_team_attendance",
  "approve_team_attendance",
  "manage_inventory",
  // Accounting — owner finances (Phase SC-2b). Expense ledger + unified
  // staff-cost ledger + monthly gross/net P&L. Admin-ceiling by default;
  // never granted to teacher / staff / student / parent. Mirrors
  // `Permission.VIEW_ACCOUNTING` / `MANAGE_ACCOUNTING` on the BE.
  "view_accounting",
  "manage_accounting",
  // Bookings (study centre)
  "manage_zones",
  "manage_bookings",
  "book_seat",
  // Analytics
  "view_analytics",
  "view_own_analytics",
  // Settings & access control
  "manage_settings",
  "manage_roles",
  // Forms (Phase A1)
  "manage_forms",
  "view_forms",
  // Staff directory (Phase J2 — 2026-05-14)
  // Read-only ceiling for the People → Staff roster. Granted by default
  // to admin, teacher, staff. Mirrors `Permission.VIEW_STAFF` on the BE.
  "view_staff",
  // Phase J2.d (2026-05-15) — HR-grade writes against the staff
  // directory. Currently bounded to suspend / reactivate of an
  // existing User row. Default-granted to ``admin`` only; ``owner``
  // and ``super_admin`` keep wildcard. Mirrors
  // ``Permission.MANAGE_STAFF`` on the BE.
  "manage_staff",
] as const;

export type PermissionId = (typeof PERMISSION_IDS)[number];

/** Marker constant referenced by the Playwright drift test. Bump whenever
 *  the catalog above changes so the test anchor moves with it. */
export const PERMISSION_CATALOG_VERSION = 8;

/**
 * Per-row vertical / module gating descriptor.
 *
 * Both fields are intentionally OR-semantics — set them only when the
 * row should be hidden for a particular shape of tenant:
 *
 * - `verticalsGate` — render only when the tenant's vertical is in this
 *   list. e.g. `["study_centre"]` on the Bookings group hides bookings
 *   permissions on school + coaching tenants.
 * - `modulesGate` — render only when ANY ONE of these module keys is in
 *   the tenant's `enabled_modules` set. e.g. `["transport"]` on the
 *   `manage_transport` permission hides that row when the transport
 *   module is disabled.
 *
 * Both gates omitted = always render (default catalog behaviour).
 *
 * Iron Rule #5: this is an additive change to the catalog, not a rename.
 * Existing rows that don't carry either field render as before, so the
 * upgrade is backwards-compatible with the FE Playwright catalog drift
 * test anchored on `PERMISSION_CATALOG_VERSION`.
 */
export interface PermissionGate {
  /** Allow-list of verticals; omit to render on all verticals. */
  verticalsGate?: readonly TenantVerticalLiteral[];
  /** Any-of allow-list of module keys; omit to render regardless of modules. */
  modulesGate?: readonly string[];
}

/**
 * Single permission row inside a group. The runtime shape is the
 * existing `{ id, label, helper }` plus optional `PermissionGate`
 * fields; consumers that ignore the gate fields keep working unchanged.
 */
export interface PermissionRow extends PermissionGate {
  id: PermissionId;
  label: string;
  /** ≤ 100 chars; rendered as a muted helper line under the checkbox row. */
  helper: string;
}

/**
 * Human-friendly grouping for the editor UI. Order in this list is the
 * render order in the editor sheet (predictable scanning > alphabetical).
 */
export interface PermissionGroup extends PermissionGate {
  /** Stable group key — used for collapsible-state persistence in localStorage. */
  key: string;
  /** UI label shown above the group's checkbox column. */
  label: string;
  /** One-line gist for the group's tooltip / aria-description. */
  description: string;
  permissions: ReadonlyArray<PermissionRow>;
}

export const PERMISSION_GROUPS: ReadonlyArray<PermissionGroup> = [
  {
    key: "students",
    label: "Students",
    description: "Read and manage the student roster and parent links.",
    permissions: [
      {
        id: "view_students",
        label: "View students",
        helper: "See student profiles, parent contacts, and academic history.",
      },
      {
        id: "manage_students",
        label: "Manage students",
        helper: "Edit profiles, archive records, and merge duplicates.",
      },
      {
        id: "import_students",
        label: "Import students",
        helper: "Bulk-upload roster CSVs via the /imports/students page.",
      },
    ],
  },
  {
    key: "academic",
    label: "Academic",
    description: "Batches, sections, timetables, subjects.",
    permissions: [
      {
        id: "view_batches",
        label: "View batches",
        helper: "See batch / class / section rosters and capacity.",
      },
      {
        id: "manage_batches",
        label: "Manage batches",
        helper: "Create / edit batches, transfer students, set capacity.",
      },
      {
        id: "view_timetable",
        label: "View timetable",
        helper: "See the institution-wide schedule.",
        modulesGate: ["timetable"],
      },
      {
        id: "manage_timetable",
        label: "Manage timetable",
        helper: "Edit periods, assign teachers, swap sections.",
        modulesGate: ["timetable"],
      },
      { id: "view_subjects", label: "View subjects", helper: "Browse the subject catalog." },
      {
        id: "manage_subjects",
        label: "Manage subjects",
        helper: "Create / edit subjects, link to grades and batches.",
      },
    ],
  },
  {
    key: "assessment",
    label: "Assessment",
    description: "Tests, assignments, grading, the question bank.",
    permissions: [
      {
        id: "create_tests",
        label: "Create tests",
        helper: "Build new test definitions and templates.",
      },
      {
        id: "grade_tests",
        label: "Grade tests",
        helper: "Submit marks and feedback for completed tests.",
      },
      { id: "view_results", label: "View results", helper: "See test scores across batches." },
      {
        id: "manage_results",
        label: "Manage results",
        helper: "Edit / publish / unpublish result sheets.",
      },
      { id: "create_assignments", label: "Create assignments", helper: "Author new assignments." },
      {
        id: "grade_assignments",
        label: "Grade assignments",
        helper: "Mark submissions and provide feedback.",
      },
      {
        id: "manage_question_bank",
        label: "Manage question bank",
        helper: "Add / edit / archive question rows in the bank.",
      },
    ],
  },
  {
    key: "content",
    label: "Content",
    description: "Course material and uploads.",
    permissions: [
      {
        id: "upload_content",
        label: "Upload content",
        helper: "Add lesson files, videos, and reference material.",
      },
      {
        id: "manage_courses",
        label: "Manage courses",
        helper: "Create / edit courses and link them to batches.",
      },
    ],
  },
  {
    key: "live_classes",
    label: "Live classes",
    description: "Start and join the live-class runtime.",
    permissions: [
      {
        id: "start_live_class",
        label: "Start live class",
        helper: "Initiate a live session for an enrolled batch.",
      },
      {
        id: "join_live_class",
        label: "Join live class",
        helper: "Enter an active live session as a participant.",
      },
    ],
  },
  {
    key: "communication",
    label: "Communication",
    description: "Announcements, notifications, and parent comms.",
    permissions: [
      {
        id: "send_announcements",
        label: "Send announcements",
        helper: "Author and publish broadcast messages.",
      },
      {
        id: "view_announcements",
        label: "View announcements",
        helper: "Read announcements in the portal.",
      },
      {
        id: "manage_notifications",
        label: "Manage notifications",
        helper: "Edit notification preferences and channels.",
      },
      {
        id: "parent_communication",
        label: "Parent communication",
        helper: "Direct messages to parents from the portal.",
      },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    description: "Fees, attendance, transport, admissions, payroll.",
    permissions: [
      {
        id: "manage_fees",
        label: "Manage fees",
        helper: "Edit fee plans, raise invoices, record payments.",
      },
      { id: "view_fees", label: "View fees", helper: "See fee structures and payment status." },
      {
        id: "pay_fees",
        label: "Pay fees",
        helper: "Submit payments through the portal (parent / student surface).",
      },
      {
        id: "manage_attendance",
        label: "Manage attendance",
        helper: "Mark / edit / approve attendance entries.",
        modulesGate: ["attendance"],
      },
      {
        id: "view_attendance",
        label: "View attendance",
        helper: "Read attendance reports and roll-ups.",
        modulesGate: ["attendance"],
      },
      {
        id: "manage_transport",
        label: "Manage transport",
        helper: "Buses, routes, GPS tracking, transport attendance.",
        modulesGate: ["transport"],
      },
      {
        id: "manage_admissions",
        label: "Manage admissions",
        helper: "Edit enquiries, advance stages, send offer letters.",
      },
      {
        id: "manage_certificates",
        label: "Manage certificates",
        helper: "Issue bonafide / TC / completion certificates.",
      },
      {
        id: "manage_inventory",
        label: "Manage inventory",
        helper: "Books, uniforms, lab consumables, IT assets.",
      },
    ],
  },
  {
    key: "bookings",
    label: "Bookings (study centre)",
    description: "Seat zones and bookings for study-centre verticals.",
    verticalsGate: ["study_centre"],
    permissions: [
      { id: "manage_zones", label: "Manage zones", helper: "Define seat zones and capacity." },
      {
        id: "manage_bookings",
        label: "Manage bookings",
        helper: "Approve / cancel / reassign bookings.",
      },
      {
        id: "book_seat",
        label: "Book a seat",
        helper: "Place a booking from the student / member surface.",
      },
    ],
  },
  {
    key: "accounting",
    label: "Accounting",
    description: "Owner finances — expense ledger, staff costs, and profit reports.",
    // Rendered only when the tenant has the `accounting` module enabled
    // (default-ON for study centres). Admin-ceiling; not granted to
    // teacher / staff by default.
    modulesGate: ["accounting"],
    permissions: [
      {
        id: "view_accounting",
        label: "View accounting",
        helper: "See the expense ledger, staff costs, and the monthly profit & loss report.",
      },
      {
        id: "manage_accounting",
        label: "Manage accounting",
        helper: "Log / edit / delete expenses and staff costs.",
      },
    ],
  },
  {
    key: "payroll",
    label: "Payroll",
    description: "Owner finances — monthly pay runs feeding the staff-cost P&L line.",
    // Rendered only when the tenant has the `payroll` module enabled
    // (default-ON for study centres). Admin-ceiling; not granted to
    // teacher / staff by default.
    modulesGate: ["payroll"],
    permissions: [
      {
        id: "view_payroll",
        label: "View payroll",
        helper: "See pay runs, payslips, and the monthly payroll summary.",
      },
      {
        id: "manage_payroll",
        label: "Manage payroll",
        helper: "Generate / edit / finalize monthly pay runs and payslips.",
      },
      {
        id: "view_team_pay",
        label: "View team pay",
        helper: "See pay terms, what is owed, statements, advances and payments.",
      },
      {
        id: "manage_team_pay",
        label: "Manage team pay",
        helper: "Edit pay terms, record payments, advances, incentives and deductions.",
      },
      {
        id: "manage_team_attendance",
        label: "Mark team attendance",
        helper: "Mark days for the team and change attendance settings.",
      },
      {
        id: "approve_team_attendance",
        label: "Approve team attendance",
        helper: "Approve or reject check-ins and leave requests.",
      },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    description: "Cross-tenant or self-only analytics.",
    permissions: [
      {
        id: "view_analytics",
        label: "View analytics",
        helper: "Cross-cutting dashboards and reports.",
      },
      {
        id: "view_own_analytics",
        label: "View own analytics",
        helper: "See only this user's slice of the data.",
      },
    ],
  },
  {
    key: "settings",
    label: "Settings & access control",
    description: "The editor's own permissions live here.",
    permissions: [
      {
        id: "manage_settings",
        label: "Manage settings",
        helper: "Edit tenant-level configuration.",
      },
      {
        id: "manage_roles",
        label: "Manage roles",
        helper: "Author designations and edit RBAC overrides (this surface).",
      },
    ],
  },
  {
    key: "forms",
    label: "Forms",
    description: "Tenant-scoped form runtime and editor.",
    permissions: [
      {
        id: "manage_forms",
        label: "Manage forms",
        helper: "Create / edit / archive forms in the IA portal.",
      },
      {
        id: "view_forms",
        label: "View forms",
        helper: "Read form definitions and submitted responses.",
      },
    ],
  },
  {
    key: "staff",
    label: "Staff",
    description: "Internal user roster for People → Staff.",
    permissions: [
      {
        id: "view_staff",
        label: "View staff",
        helper: "See the institution's staff roster, roles, and invite status.",
      },
      {
        id: "manage_staff",
        label: "Manage staff",
        helper: "Suspend or reactivate staff accounts. Self-target is always blocked.",
      },
    ],
  },
];

/**
 * Tenant-shape context passed to `filterGroupsForTenant` so the helper
 * can evaluate `verticalsGate` + `modulesGate` per row.
 *
 * `enabledModules` is a `ReadonlySet<string>` instead of a positional
 * array because the consumer hooks (`useEnabledModules`, the
 * BE dashboard summary) already materialise a Set — passing it through
 * avoids quadratic membership checks while folding the catalog.
 */
export interface PermissionFilterContext {
  vertical: TenantVerticalLiteral;
  enabledModules: ReadonlySet<string>;
}

function _passesGates(
  gate: Pick<PermissionGate, "verticalsGate" | "modulesGate">,
  ctx: PermissionFilterContext,
): boolean {
  if (gate.verticalsGate && !gate.verticalsGate.includes(ctx.vertical)) {
    return false;
  }
  if (gate.modulesGate && !gate.modulesGate.some((m) => ctx.enabledModules.has(m))) {
    return false;
  }
  return true;
}

/**
 * Phase F-finale-3 (Iron Rule #11/#17 — close Finding #1):
 *
 * Returns the subset of `PERMISSION_GROUPS` that should be visible to
 * the calling tenant. Group-level gates are evaluated first; rows
 * inside surviving groups are then filtered by their own gates.
 * Empty groups (every row hidden) are dropped from the output so the
 * designation editor doesn't render dangling legends.
 *
 * Pure / side-effect-free. Safe to call inside `useMemo`.
 *
 * Iron Rule #2 reminder: the BE re-clamps any override against the
 * full catalog on save, so a deliberately crafted POST trying to grant
 * a hidden permission still returns 422 `AZ_OVERRIDE_EXCEEDS_CEILING`
 * (or `AZ_OVERRIDE_OUT_OF_VERTICAL` once Slice 4 ships). This filter
 * is a UX layer over the BE ceiling — not the only line of defence.
 */
export function filterGroupsForTenant(
  groups: ReadonlyArray<PermissionGroup>,
  ctx: PermissionFilterContext,
): PermissionGroup[] {
  const out: PermissionGroup[] = [];
  for (const group of groups) {
    if (!_passesGates(group, ctx)) continue;
    const rows = group.permissions.filter((row) => _passesGates(row, ctx));
    if (rows.length === 0) continue;
    out.push({ ...group, permissions: rows });
  }
  return out;
}

/**
 * Convenience: flat allow-list of permission IDs that survive the
 * group + row filter for the supplied tenant context. Used by
 * `SuggestPermissionsDrawer` to drop AI suggestions that target
 * hidden permissions (defence-in-depth on top of `filterGroupsForTenant`).
 */
export function allowedPermissionIdsForTenant(
  groups: ReadonlyArray<PermissionGroup>,
  ctx: PermissionFilterContext,
): Set<PermissionId> {
  const result = new Set<PermissionId>();
  for (const group of filterGroupsForTenant(groups, ctx)) {
    for (const row of group.permissions) result.add(row.id);
  }
  return result;
}

/**
 * Default permissions per role — mirror of BE `ROLE_PERMISSIONS`. This is
 * what every designation under the role STARTS with before its
 * `permission_overrides` are applied. The editor renders these as
 * "default-on" checkboxes so the user can see what's bundled vs what
 * they're toggling on top.
 *
 * `super_admin` and `owner` are wildcard on the BE (the full catalog).
 * The FE catalog above carries the canonical list — for those two roles,
 * this map returns a sentinel `"*"` so the editor renders every catalog
 * row as default-on (matching the BE's `OWNER_WILDCARD` semantics).
 */
export type RoleCeiling = "*" | ReadonlyArray<PermissionId>;

export const ROLE_DEFAULT_PERMISSIONS: Record<UserRole, RoleCeiling> = {
  super_admin: "*",
  owner: "*",
  admin: [
    "view_students",
    "manage_students",
    "import_students",
    "view_batches",
    "manage_batches",
    "view_timetable",
    "manage_timetable",
    "view_subjects",
    "manage_subjects",
    "create_tests",
    "grade_tests",
    "view_results",
    "manage_results",
    "create_assignments",
    "grade_assignments",
    "manage_question_bank",
    "upload_content",
    "manage_courses",
    "start_live_class",
    "send_announcements",
    "manage_notifications",
    "parent_communication",
    "manage_fees",
    "view_fees",
    "manage_attendance",
    "view_attendance",
    "manage_admissions",
    "manage_certificates",
    "manage_zones",
    "manage_bookings",
    // Phase SC-2b — owner finances. Admin is the de-facto owner-operator
    // for a study centre; profitability + cost ledgers sit at the same
    // ceiling as manage_settings. owner / super_admin keep wildcard.
    "view_accounting",
    "manage_accounting",
    // Phase SC-2c — owner payroll. A study-centre owner (signed in as admin)
    // runs monthly pay cycles; a finalized run's gross becomes that month's
    // authoritative staff cost in the P&L. Same owner-finance ceiling as
    // accounting; owner / super_admin keep wildcard.
    "view_payroll",
    "manage_payroll",
    // Phase SC-22d — team pay + attendance, same owner-finance ceiling.
    "view_team_pay",
    "manage_team_pay",
    "manage_team_attendance",
    "approve_team_attendance",
    "view_analytics",
    "manage_settings",
    // manage_roles granted to admin per TRD-02 §2.3 (revised 2026-05-03):
    // designation authoring is settings-adjacent; `owner` and `super_admin`
    // remain wildcard above this.
    "manage_roles",
    "manage_forms",
    "view_forms",
    "view_staff",
    // Phase J2.d — admin is the de-facto HR role; suspend / reactivate
    // lives at the same ceiling as `manage_settings` / `manage_roles`.
    "manage_staff",
  ],
  teacher: [
    "view_students",
    "view_batches",
    "view_timetable",
    "view_subjects",
    "create_tests",
    "grade_tests",
    "view_results",
    "create_assignments",
    "grade_assignments",
    "manage_question_bank",
    "upload_content",
    "start_live_class",
    "send_announcements",
    "parent_communication",
    "manage_attendance",
    "view_attendance",
    "view_own_analytics",
    "view_forms",
    "view_staff",
  ],
  // Phase H3 (2026-05-10) — staff ships with ZERO default permissions.
  // Newly invited staff users start with no implied capability — admins
  // explicitly curate per-staff overrides via the IA invite editor (with
  // AI-suggested scopes from `POST /me/first-run/staff-invites/ai-suggest`).
  // The legacy 6-permission default set is preserved on existing staff
  // users via the BE migration `scripts/migrate_staff_default_perms.py`.
  // The `staff` role's permission CEILING is decoupled from this default
  // set — see `STAFF_CEILING_BLOCKLIST` + `resolveRoleCeiling` below for
  // the wider grant-able set. Without that split the empty default would
  // collapse the ceiling and reject every override.
  staff: [],
  student: [
    "join_live_class",
    "view_announcements",
    "view_attendance",
    "view_fees",
    "pay_fees",
    "book_seat",
    "view_own_analytics",
  ],
  parent: [
    "view_announcements",
    "view_attendance",
    "view_fees",
    "pay_fees",
    "view_own_analytics",
    "parent_communication",
  ],
};

/**
 * Phase H3 (2026-05-10) — permissions that staff must NEVER hold,
 * regardless of how the IA admin curates the override list. Mirrors
 * `_STAFF_CEILING_BLOCKLIST` in `app/domains/auth/permissions.py`.
 *
 * Typed as `ReadonlyArray<string>` rather than
 * `ReadonlyArray<PermissionId>` because the BE catalog includes a few
 * permissions (`manage_integrations`, `admissions:override_stage_gate`,
 * `class_teacher_period_zero`) that the FE permissions catalog does
 * not enumerate today — they're managed from BE-only flows. The filter
 * downstream simply skips any string not present in `PERMISSION_IDS`.
 *
 * Keep this list byte-aligned with the BE constant; drift means the
 * AI suggester or the editor will offer a permission that the BE
 * rejects at write-time with `AZ_OVERRIDE_EXCEEDS_CEILING`.
 */
export const STAFF_CEILING_BLOCKLIST: ReadonlyArray<string> = [
  "manage_settings",
  "manage_roles",
  "manage_integrations",
  "admissions:override_stage_gate",
  "class_teacher_period_zero",
  // Phase J2.d — HR-grade writes against staff (suspend / reactivate).
  // Deliberately blocked from the staff role's ceiling so a designation
  // override can never hand suspend authority to a colleague-level user.
  // Admin keeps the default grant; owner/super_admin via wildcard.
  "manage_staff",
];

/**
 * Resolve the ceiling for a role into a concrete set of permission ids.
 *
 * Wildcard (super_admin / owner) expands to the full catalog. Used by
 * the editor to:
 *   1. Decide which catalog rows to render as "in ceiling" (toggleable).
 *   2. Render out-of-ceiling rows as disabled with a tooltip (the editor
 *      stays HONEST about what's possible instead of silently hiding).
 *
 * Phase H3 — `staff` is special-cased: its ceiling is **decoupled**
 * from `ROLE_DEFAULT_PERMISSIONS["staff"]` (which is empty post-H3).
 * Returning the empty default set as the ceiling would collapse the
 * editor and reject every AI-suggested override. Instead, the staff
 * ceiling is "every defined permission MINUS `STAFF_CEILING_BLOCKLIST`",
 * mirroring `_staff_ceiling()` on the BE.
 */
export function resolveRoleCeiling(role: UserRole): Set<PermissionId> {
  if (role === "staff") {
    const blocklist = new Set<string>(STAFF_CEILING_BLOCKLIST);
    return new Set(PERMISSION_IDS.filter((p) => !blocklist.has(p)));
  }
  const raw = ROLE_DEFAULT_PERMISSIONS[role];
  if (raw === "*") {
    return new Set(PERMISSION_IDS);
  }
  return new Set(raw);
}

/**
 * Given a designation row, compute the diff vs its base role's defaults:
 *  - granted   — overrides that aren't in the role's default set (added)
 *  - revoked   — base permissions NOT present in the resolved set (removed)
 *
 * The BE doesn't ship a "revoked" concept today — `permission_overrides`
 * is additive on top of the role default. So `revoked` is always empty
 * with the current schema; the function still computes it so we're ready
 * for the day overrides become bidirectional (Phase F-followup if needed).
 *
 * Returned counts are what the DesignationsPage list shows in the
 * "Overrides" column.
 */
export interface DesignationOverrideDiff {
  granted: PermissionId[];
  revoked: PermissionId[];
}

export function computeDesignationDiff(
  role: UserRole,
  permissionOverrides: ReadonlyArray<string>,
): DesignationOverrideDiff {
  const ceiling = resolveRoleCeiling(role);
  const defaults = ROLE_DEFAULT_PERMISSIONS[role];
  const defaultSet = new Set<PermissionId>(defaults === "*" ? PERMISSION_IDS : defaults);

  const granted: PermissionId[] = [];
  for (const override of permissionOverrides) {
    if (!ceiling.has(override as PermissionId)) {
      // Outside ceiling — silently dropped by BE; we don't surface here either.
      continue;
    }
    if (!defaultSet.has(override as PermissionId)) {
      granted.push(override as PermissionId);
    }
  }

  // `revoked` reserved for the future bidirectional override flow.
  return { granted, revoked: [] };
}

/**
 * Resolve a user's effective permission set from their role + overrides,
 * mirroring `app.domains.auth.permissions.resolve_permissions` on the BE.
 *
 * Used by FE permission gates (e.g. hide the "New designation" CTA when
 * the current user lacks `manage_roles`). The BE remains the source of
 * truth — this is purely a UX optimisation so we don't show CTAs that
 * 403 on click. Out-of-ceiling and unknown overrides are silently dropped
 * to match BE behaviour exactly.
 */
export function resolveUserPermissions(
  role: UserRole,
  permissionOverrides: ReadonlyArray<string>,
  permissionMode: "additive" | "replace" = "additive",
): Set<PermissionId> {
  const ceiling = resolveRoleCeiling(role);
  const raw = ROLE_DEFAULT_PERMISSIONS[role];
  const out = new Set<PermissionId>(
    permissionMode === "replace" ? [] : raw === "*" ? PERMISSION_IDS : raw,
  );
  for (const override of permissionOverrides) {
    const cast = override as PermissionId;
    if (ceiling.has(cast)) out.add(cast);
  }
  return out;
}

/**
 * True when the user's resolved permission set contains the requested
 * permission. Convenience wrapper for the common gate.
 */
export function userHasPermission(
  role: UserRole,
  permissionOverrides: ReadonlyArray<string>,
  permission: PermissionId,
  permissionMode: "additive" | "replace" = "additive",
): boolean {
  return resolveUserPermissions(role, permissionOverrides, permissionMode).has(permission);
}
